/**
 * RecommendationRow — ranked product recommendation row for persona affinity UI.
 * Shared between ProductsLookupCard (chat) and ProductsLens.
 */

import { Package } from 'lucide-react';

export interface ProductRecommendationRow {
  product_id: string;
  product_name: string;
  affinity_score?: number | null;
  affinity_tier?: string | null;
  rationale?: string | null;
  scenario_name?: string | null;
  source?: 'persona_affinity' | 'agent' | string;
}

const TIER_CHIP: Record<string, string> = {
  Top: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  High: 'bg-teal-100 text-teal-700 border-teal-200',
  Medium: 'bg-slate-100 text-slate-600 border-slate-200',
  Low: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

function tierChipClass(tier: string | null | undefined): string {
  if (!tier) return 'bg-slate-100 text-slate-500 border-slate-200';
  return TIER_CHIP[tier] ?? 'bg-slate-100 text-slate-500 border-slate-200';
}

export function RecommendationRow({
  recommendation: r,
  rank,
  imageUrl,
  compact = false,
}: {
  recommendation: ProductRecommendationRow;
  rank?: number;
  imageUrl?: string | null;
  compact?: boolean;
}) {
  const sourceLabel =
    r.source === 'agent' ? 'strategy' : r.source === 'persona_affinity' ? 'affinity' : null;

  return (
    <div
      className={`flex gap-2.5 rounded-lg border border-slate-100 bg-white ${
        compact ? 'px-2 py-1.5' : 'px-3 py-2.5'
      }`}
    >
      {rank != null && (
        <span className="text-[10px] font-mono text-slate-400 shrink-0 w-4 pt-0.5">{rank}</span>
      )}
      <div className="shrink-0 w-10 h-10 rounded-md border border-slate-100 bg-slate-50 overflow-hidden flex items-center justify-center">
        {imageUrl ? (
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <Package size={16} className="text-slate-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`font-semibold text-slate-800 ${compact ? 'text-[11px]' : 'text-xs'}`}
          >
            {r.product_name}
          </span>
          {r.affinity_tier && (
            <span
              className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${tierChipClass(
                r.affinity_tier,
              )}`}
            >
              {r.affinity_tier}
            </span>
          )}
          {r.affinity_score != null && (
            <span className="text-[10px] text-slate-500 font-mono">
              {Number(r.affinity_score).toFixed(1)}
            </span>
          )}
          {sourceLabel && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-100">
              {sourceLabel}
            </span>
          )}
          {r.scenario_name && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100 truncate max-w-[140px]">
              {r.scenario_name}
            </span>
          )}
        </div>
        {r.rationale && (
          <p
            className={`text-slate-500 mt-0.5 leading-snug ${compact ? 'text-[10px]' : 'text-[11px]'}`}
          >
            {r.rationale}
          </p>
        )}
      </div>
    </div>
  );
}
